import { isTelehealthProduct } from "~/hec/types/product";
import { sanitizeHtml } from "~/utils/sanitize-html";

import type { ProductAccordionSection } from "./types";

/**
 * The accordions ET-213 hides on a telehealth PDP. Everything else the catalog
 * populates (Description, Ingredients, Shipping Restrictions, Size Chart) still
 * renders. ET-213 also hid the long description; ET-509 brought it back as the
 * "Product Description" accordion, while the info column keeps the short one.
 */
export const HIDDEN_ACCORDIONS_ON_TELEHEALTH = new Set([
  "warnings",
  "instructions",
]);

/**
 * The catalog-populated PDP accordion sections, in display order. One source
 * for the accordion block AND the short description's "Read More" link, which
 * targets that block (`#collapsible-description`) — so the link can never
 * point at a block that does not render (COM-180 review).
 */
export function productAccordionSections(
  p: Record<string, unknown>,
): ProductAccordionSection[] {
  const isTelehealth = isTelehealthProduct(p as { c_ownerClass?: string });
  return [
    {
      key: "description",
      label: "Product Description",
      // SFRA's `longDescription`: the site long description, falling
      // back to the catalog one (org_hec `decorators/description.js`).
      // Only the fallback is sanitised: it is the surface COM-180 added, and
      // the site field renders unsanitised as it always has (SFRA prints it
      // unencoded; sanitising it would strip embeds that render today).
      content:
        (p.c_siteLongDescription as string) ||
        (typeof p.longDescription === "string"
          ? sanitizeHtml(p.longDescription)
          : ""),
    },
    {
      key: "details",
      label: "Product Details",
      content: p.c_productDetails as string,
    },
    {
      key: "ingredients",
      label: "Ingredients",
      content: p.c_ingredients as string,
    },
    {
      key: "instructions",
      label: "Instructions",
      content: p.c_instructions as string,
    },
    {
      key: "warnings",
      label: "Warnings",
      content: p.c_warnings as string,
    },
    {
      key: "shippingRestrictions",
      label: "Shipping Restrictions",
      content: p.c_shippingRestrictions as string,
    },
    {
      key: "sizeChart",
      label: (p.c_sizeChartTitle as string) || "Size Chart",
      content: p.c_sizeChart as string,
    },
  ].filter(
    s =>
      s.content &&
      !(isTelehealth && HIDDEN_ACCORDIONS_ON_TELEHEALTH.has(s.key)),
  );
}

/**
 * The PDP renders the sections in two places (the short description's Read
 * More gate and the accordion block itself), each in its own `<Await>` callback
 * over the same resolved product object. Memoising per object means
 * `sanitizeHtml(longDescription)` runs once per product, not once per caller.
 * Loader data is never mutated after it resolves, so object identity is a safe
 * key.
 */
const sectionsByProduct = new WeakMap<object, ProductAccordionSection[]>();

/** `productAccordionSections`, computed once per product object. */
export function accordionSectionsFor(
  p: Record<string, unknown>,
): ProductAccordionSection[] {
  let sections = sectionsByProduct.get(p);
  if (!sections) {
    sections = productAccordionSections(p);
    sectionsByProduct.set(p, sections);
  }
  return sections;
}

/**
 * Will the PDP's accordion block (`#collapsible-description`) render? Yes when
 * the product populates any section, or a Builder accordion (Delivery &
 * Returns, Loyalty) is present.
 */
export function hasAccordionBlock(
  p: Record<string, unknown>,
  hasBuilderAccordions: boolean,
): boolean {
  return hasBuilderAccordions || accordionSectionsFor(p).length > 0;
}
